import cx from 'classnames';
import { MDText } from 'i18n-react';
import isNumber from 'lodash/isNumber';

import cypressIds from 'Components/cypress_ids.json';
import { useBidCount } from 'Components/shared/VehicleCards/VehicleCard/VehicleCardView/VehicleCardView.helpers';
import { VehicleOriginalPrice } from 'Components/shared/VehicleOriginalPrice/VehicleOriginalPrice';
import { useVehicleData } from 'Context/VehicleContext/VehicleDataContext';
import { mapStateSlugToListingsTableSaleType } from 'Stores/AdditionalVehicleDetailsStore/AdditionalVehicleDetailsStore.helpers';
import { ForSaleCategory } from 'Types/enquiryStates';
import { applyCypressData } from 'Utilities/helpers';
import { calculateShouldVehicleShowOriginalPrice } from 'Utilities/helpers/calculateShouldVehicleShowOriginalPrice';
import { formatVehicleOriginalPrice } from 'Utilities/helpers/formatVehicleOriginalPrice';
import useFeatureToggle, { FEATURES } from 'Utilities/hooks/useFeatureToggle';
import { useGetAdditionalVehicleDetails } from 'Utilities/hooks/vehicle/useGetAdditionalVehicleDetails';
import { useIsBinVehicleProcessing } from 'Utilities/hooks/vehicle/useIsBinVehicleProcessing';

import { HighestOffer } from '../HighestOffer/HighestOffer';

import type { BadgeVariant } from './StatusBadge/StatusBadge';
import { StatusBadge } from './StatusBadge/StatusBadge';
import priceAndSaleInfoTexts from './PriceAndSaleInfo.texts';

import styles from './PriceAndSaleInfo.module.scss';

const LocalT = new MDText(priceAndSaleInfoTexts);

export const PriceAndSaleInfo: React.FC<{ badgeVariant?: BadgeVariant }> = ({ badgeVariant }) => {
	const isBinDiscountVisibilityEnabled = useFeatureToggle(FEATURES.binDiscountVisibility);
	const isLiveSaleDiscountVisibilityEnabled = useFeatureToggle(FEATURES.liveSaleDiscountVisibility);
	const isHideDiscountWhenReheatedEnabled = useFeatureToggle(FEATURES.hideDiscountWhenReheated);

	const { bidding, id, state } = useVehicleData();
	const { bidCount: bidCountValue } = useBidCount({ id, state });
	const bidCount = bidCountValue ?? bidding.activeBidCount;
	const slug = state?.slug;
	const price = bidding.displayPrice.value;
	const displayPrice = bidding.displayPrice.displayValue;
	const highestBidOrOffer = bidding.highestBidOrOffer?.displayValue;
	const shouldShowHighestOffer = slug === ForSaleCategory.forSale && highestBidOrOffer;
	const saleType = mapStateSlugToListingsTableSaleType(slug);

	const { expiryDate, originalPrice, reheatCount } = useGetAdditionalVehicleDetails({ id, saleType }) ?? {};
	const isBinVehicleProcessing = useIsBinVehicleProcessing({ expiryDate, slug });
	const vehicleOriginalPrice = formatVehicleOriginalPrice({ originalPrice, price, slug });

	const shouldShowVAT = bidding.isVatQualifying;
	const shouldShowPrice = price > 0;

	const shouldShowOriginalPrice = calculateShouldVehicleShowOriginalPrice({
		isBinDiscountVisibilityEnabled,
		isLiveSaleDiscountVisibilityEnabled,
		slug,
		vehicleOriginalPrice,
	});

	const shouldHideDiscountWhenReheated = isHideDiscountWhenReheatedEnabled && isNumber(reheatCount);

	if (!shouldShowPrice && !badgeVariant) {
		return null;
	}

	const isLiveSaleVehicle = slug === ForSaleCategory.saleActive;

	return (
		<>
			<div className={cx(styles.container, { [styles.hasVat]: shouldShowVAT })}>
				{shouldShowPrice ? (
					<>
						{shouldShowOriginalPrice && !shouldHideDiscountWhenReheated ? (
							<div className={styles.originalPriceContainer}>
								<VehicleOriginalPrice price={vehicleOriginalPrice} />
							</div>
						) : null}
						<div className={styles.priceAndBidCountContainer}>
							<div className={styles.priceContainer}>
								<span className={styles.price} {...applyCypressData(cypressIds.text.sidebarPrice)}>
									{displayPrice} {shouldShowVAT ? <span className={styles.vat}> +VAT</span> : null}
								</span>
							</div>
							{isLiveSaleVehicle && (
								<span className={styles.bidCount}>
									{bidCount === 1
										? LocalT.translate('bidCount.singular', { bidCount })
										: LocalT.translate('bidCount.plural', { bidCount })}
								</span>
							)}
						</div>
					</>
				) : null}
				{badgeVariant ? (
					<div {...applyCypressData(cypressIds.text.sidebarSaleInfoTag)}>
						<StatusBadge variant={isBinVehicleProcessing ? 'saleOverProcessing' : badgeVariant} />
					</div>
				) : null}
			</div>
			{shouldShowHighestOffer ? <HighestOffer offer={highestBidOrOffer} /> : null}
		</>
	);
};
